import { useSearchParams, useNavigate } from "react-router-dom";
import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import Footer from "@/components/Footer";
import SearchBar from "@/components/SearchBar";
import { Card, CardContent } from "@/components/ui/card";
import { Avatar, AvatarImage, AvatarFallback } from "@/components/ui/avatar";
import { MapPin, Users, Bed, Building2, BadgeCheck, Tag } from "lucide-react";
import { useAuthState } from "@/hooks/useAuthState";
import { getCoverImageUrl } from "@/lib/imageMap";
import { getUserImageUrl } from "@/config/api";
import { SearchService, SearchResult, SearchFilters } from "@/services/searchService";
import TopHeroTemplate from "@/components/layout/TopHeroTemplate";
import heroMuroAlto1 from "@/assets/hero-muro-alto-1.jpg";
import heroMuroAlto2 from "@/assets/hero-muro-alto-2.jpg";
import heroMuroAlto3 from "@/assets/hero-muro-alto-3.jpg";
import heroMuroAlto4 from "@/assets/hero-muro-alto-4.jpg";
import heroMuroAlto5 from "@/assets/hero-muro-alto-5.jpg";

const PAGE_SIZE = 8;
const HERO_IMAGES = [heroMuroAlto1, heroMuroAlto2, heroMuroAlto3, heroMuroAlto4, heroMuroAlto5];

const getDevelopmentRoute = (developmentName: string | null | undefined): string | null => {
  if (!developmentName) return null;
  const nameLower = developmentName.toLowerCase().trim();
  if (nameLower.includes("porto 2 life") || nameLower.includes("porto2life")) {
    return "/empreendimentos/porto-2-life";
  }
  if (nameLower.includes("exclusive gramado")) {
    return "/empreendimentos/exclusive-gramado";
  }
  return null;
};

const SaleSearchResults = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const empreendimento = searchParams.get("empreendimento") || "";
  const hospedes = searchParams.get("hospedes") || "";
  const { isAuthenticated, userType, userName, userPhoto, userId } = useAuthState();

  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [totalCount, setTotalCount] = useState<number | null>(null);
  const [hasMore, setHasMore] = useState(false);
  const [nextCursor, setNextCursor] = useState<number | null>(null);
  const loadMoreRef = useRef<HTMLDivElement | null>(null);

  const randomHeroImage = useMemo(
    () => HERO_IMAGES[Math.floor(Math.random() * HERO_IMAGES.length)],
    [],
  );

  const buildFilters = useCallback((): SearchFilters => {
    return {
      empreendimento:
        empreendimento && empreendimento.trim() !== ""
          ? empreendimento.trim()
          : undefined,
      hospedes: hospedes ? parseInt(hospedes, 10) : undefined,
      forSale: true,
    };
  }, [empreendimento, hospedes]);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      try {
        setLoading(true);
        setError(null);
        setSearchResults([]);
        setNextCursor(null);
        setHasMore(false);
        setTotalCount(null);

        const page = await SearchService.searchPropertiesPage({
          ...buildFilters(),
          limit: PAGE_SIZE,
          cursor: null,
          includeTotal: true,
        });

        if (cancelled) return;
        setSearchResults(page.items);
        setNextCursor(page.nextCursor);
        setHasMore(page.hasMore);
        if (typeof page.total === "number") setTotalCount(page.total);
      } catch (e) {
        if (!cancelled) {
          console.error("Erro na busca de venda:", e);
          setError("Erro ao buscar cotas à venda. Tente novamente.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [empreendimento, hospedes, buildFilters]);

  const loadMore = useCallback(async () => {
    if (!hasMore || loadingMore || loading) return;
    try {
      setLoadingMore(true);
      const cursor =
        nextCursor ??
        (searchResults.length
          ? searchResults[searchResults.length - 1].property.id
          : null);
      if (cursor == null) return;

      const page = await SearchService.searchPropertiesPage({
        ...buildFilters(),
        limit: PAGE_SIZE,
        cursor,
        includeTotal: false,
      });

      setSearchResults((prev) => {
        const seen = new Set(prev.map((r) => r.property.id));
        const merged = [...prev];
        for (const row of page.items) {
          if (!seen.has(row.property.id)) {
            seen.add(row.property.id);
            merged.push(row);
          }
        }
        return merged;
      });
      setNextCursor(page.nextCursor);
      setHasMore(page.hasMore);
    } catch (e) {
      console.error("Erro ao carregar mais:", e);
    } finally {
      setLoadingMore(false);
    }
  }, [hasMore, loadingMore, nextCursor, loading, buildFilters, searchResults]);

  useEffect(() => {
    const el = loadMoreRef.current;
    if (!el || !hasMore || loading) return;
    const obs = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting && hasMore && !loadingMore) loadMore();
      },
      { rootMargin: "120px" },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [hasMore, loadingMore, loading, loadMore]);

  const headingCount = totalCount != null ? totalCount : searchResults.length;

  return (
    <div className="min-h-screen bg-background">
      <TopHeroTemplate
        backgroundImage={randomHeroImage}
        navbarProps={{
          isAuthenticated,
          isOwner: userType === "owner",
          userName,
          userPhoto,
          userId,
          userType,
          cleanHeader: true,
          rightActionsCard: true,
        }}
      >
        <div className="relative">
          <h1 className="text-2xl sm:text-4xl md:text-6xl font-bold text-white mb-3 drop-shadow-lg leading-tight">
            Cotas à venda
          </h1>
          <p className="text-sm sm:text-xl md:text-2xl text-white/95 mb-6 drop-shadow-md leading-relaxed">
            Encontre cotas disponíveis para compra nos empreendimentos parceiros
          </p>
        </div>
        <div className="mx-auto mt-4 sm:mt-6 w-full max-w-none">
          <SearchBar compactHeight saleSearch />
        </div>
      </TopHeroTemplate>

      <section className="py-12 container mx-auto px-4">
        <div className="mb-8">
          <h2 className="text-3xl font-bold text-foreground mb-2">
            {!loading && headingCount === 0
              ? "Nenhuma cota encontrada"
              : `${loading ? "…" : headingCount} ${headingCount === 1 ? "cota" : "cotas"} à venda`}
            {empreendimento && ` no ${empreendimento}`}
          </h2>
          <p className="text-sm text-muted-foreground">
            Imóveis marcados pelos proprietários Live como disponíveis para venda de cota.
          </p>
        </div>

        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4" />
            <p className="text-muted-foreground">Carregando cotas...</p>
          </div>
        ) : error ? (
          <div className="text-center py-12">
            <p className="text-destructive text-lg mb-2">Erro na busca</p>
            <p className="text-muted-foreground">{error}</p>
          </div>
        ) : searchResults.length === 0 ? (
          <div className="text-center py-12">
            <p className="text-muted-foreground text-lg">Nenhuma cota à venda neste empreendimento</p>
            <p className="text-muted-foreground">Tente outro resort ou volte mais tarde</p>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
              {searchResults.map((result) => {
                const property = result.property;
                const coverSrc = getCoverImageUrl(property, true);
                return (
                  <Card
                    key={property.id}
                    className="group cursor-pointer overflow-hidden border shadow-soft hover:shadow-medium transition-all duration-300 hover:-translate-y-1"
                    onClick={() => {
                      window.open(
                        `/imovel-venda/${property.id}`,
                        "_blank",
                        "noopener,noreferrer",
                      );
                    }}
                  >
                    <div className="relative aspect-[16/10] overflow-hidden bg-muted">
                      <img
                        src={coverSrc}
                        alt={property.title}
                        className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                        loading="lazy"
                        onError={(e) => {
                          const img = e.currentTarget;
                          if (img.src.includes("/thumb/")) {
                            img.src = getCoverImageUrl(property, false);
                          }
                        }}
                      />
                      <div className="absolute top-3 right-3 bg-blue-600 text-white px-2 py-1 rounded-full flex items-center gap-1 text-xs font-semibold shadow-lg z-10">
                        <Tag className="h-3 w-3" />
                        À venda
                      </div>
                      {Number(property.is_verified) === 1 && (
                        <div className="absolute top-3 left-3 bg-blue-500 text-white px-2 py-1 rounded-full flex items-center gap-1 text-xs font-semibold shadow-lg z-10">
                          <BadgeCheck className="h-3 w-3" />
                          Verificado
                        </div>
                      )}
                      {isAuthenticated && (
                        <Avatar className="absolute bottom-3 left-3 h-9 w-9 border-2 border-background shadow-lg">
                          <AvatarImage
                            src={
                              property.owner_photo
                                ? getUserImageUrl(property.owner_id, property.owner_photo)
                                : undefined
                            }
                            alt={property.owner_name || "Proprietário"}
                          />
                          <AvatarFallback className="bg-primary text-primary-foreground text-xs">
                            {property.owner_name
                              ? property.owner_name.charAt(0).toUpperCase()
                              : "P"}
                          </AvatarFallback>
                        </Avatar>
                      )}
                    </div>

                    <CardContent className="p-3 md:p-4">
                      <h3 className="font-semibold text-base md:text-lg line-clamp-2 mb-2 leading-snug">
                        {property.title}
                      </h3>

                      {property.development_name && (() => {
                        const developmentRoute = getDevelopmentRoute(
                          property.development_name,
                        );
                        return developmentRoute ? (
                          <div
                            className="flex items-center text-primary font-medium text-sm mb-2 cursor-pointer hover:underline"
                            onClick={(e) => {
                              e.stopPropagation();
                              navigate(developmentRoute);
                            }}
                          >
                            <Building2 className="h-4 w-4 mr-1 shrink-0" />
                            <span className="line-clamp-1">{property.development_name}</span>
                          </div>
                        ) : (
                          <div className="flex items-center text-primary font-medium text-sm mb-2">
                            <Building2 className="h-4 w-4 mr-1 shrink-0" />
                            <span className="line-clamp-1">{property.development_name}</span>
                          </div>
                        );
                      })()}

                      <div className="flex items-center text-muted-foreground text-sm mb-2">
                        <MapPin className="h-4 w-4 mr-1 shrink-0" />
                        <span className="line-clamp-1">
                          {property.city} - {property.state}
                        </span>
                      </div>

                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs md:text-sm text-muted-foreground">
                        <div className="flex items-center gap-1">
                          <Bed className="h-4 w-4 shrink-0" />
                          {property.bedrooms} quartos
                        </div>
                        <div className="flex items-center gap-1">
                          <Users className="h-4 w-4 shrink-0" />
                          {property.max_guests} hóspedes
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>

            <div ref={loadMoreRef} className="h-12 w-full flex items-center justify-center mt-8" />

            {loadingMore && (
              <div className="flex justify-center py-6">
                <div className="animate-spin rounded-full h-9 w-9 border-2 border-primary border-t-transparent" />
              </div>
            )}

            {!hasMore && searchResults.length > 0 && (
              <p className="text-center text-sm text-muted-foreground mt-4">
                Fim dos resultados
              </p>
            )}
          </>
        )}
      </section>

      <Footer />
    </div>
  );
};

export default SaleSearchResults;
